import React, { useState, useEffect } from 'react';
import { SharedFileRecord } from '../types';
import { 
  fetchCloudFileRecords, 
  saveFileRecordToCloud, 
  deleteCloudFileRecord, 
  clearAllCloudFiles,
  subscribeToCloudFiles,
  detectFileType,
  formatBytes
} from '../lib/cloudFileStorage';
import { clearAllLearningData } from '../lib/learningStorage';
import { 
  Folder, FileText, Video, Presentation, Film, File, 
  UploadCloud, Download, Eye, Trash2, Search, Filter, 
  Sparkles, CheckCircle2, Cloud, RefreshCw, X, Play, Share2, Layers, Loader2
} from 'lucide-react';
import { resolveSafeFileUrl, triggerFileDownload } from '../lib/attachmentStorage';
import { uploadFileToWorker, getWorkerFileUrl, deleteFileFromWorker } from '../lib/workerFileStorage';

function getEmbedVideoUrl(url: string): string | null {
  if (!url) return null;
  const cleanUrl = url.trim();

  if (cleanUrl.startsWith('data:video') || cleanUrl.endsWith('.mp4') || cleanUrl.endsWith('.webm') || cleanUrl.endsWith('.mov')) {
    return null; // Direct file
  }

  // YouTube Shorts or standard videos
  const ytMatch = cleanUrl.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=|shorts\/))([\w-]{11})/);
  if (ytMatch && ytMatch[1]) {
    return `https://www.youtube.com/embed/${ytMatch[1]}`;
  }

  // Vimeo
  const vimeoMatch = cleanUrl.match(/vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|)(\d+)/);
  if (vimeoMatch && vimeoMatch[1]) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  }

  // Instagram (Posts, Reels, IGTV)
  const igMatch = cleanUrl.match(/(?:instagram\.com|instagr\.am)\/(?:p|reel|reels|tv)\/([A-Za-z0-9_-]+)/i);
  if (igMatch && igMatch[1]) {
    return `https://www.instagram.com/p/${igMatch[1]}/embed`;
  }

  // Aparat
  const aparatMatch = cleanUrl.match(/(?:aparat\.com\/v\/)([\w-]+)/i);
  if (aparatMatch && aparatMatch[1]) {
    return `https://www.aparat.com/video/video/embed/videohash/${aparatMatch[1]}/vt/frame`;
  }

  return null;
}

interface SharedFileManagerProps {
  initialSectionFilter?: 'all' | 'learning' | 'rehab';
  onClose?: () => void;
}

export default function SharedFileManager({ initialSectionFilter = 'all', onClose }: SharedFileManagerProps) {
  const [files, setFiles] = useState<SharedFileRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'video' | 'pdf' | 'word_ppt' | 'image'>('all');
  const [sectionFilter, setSectionFilter] = useState<'all' | 'learning' | 'rehab'>(initialSectionFilter);
  const [deleteStatus, setDeleteStatus] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  
  // Preview Modal
  const [previewFile, setPreviewFile] = useState<SharedFileRecord | null>(null);
  const [resolvedPreviewUrl, setResolvedPreviewUrl] = useState<string>('');

  useEffect(() => {
    if (previewFile) {
      // Resolve preview URL synchronously using the R2 key (preferred) or fileUrl
      const url = previewFile.key
        ? getWorkerFileUrl(previewFile.key)
        : previewFile.fileUrl?.includes('workers.dev')
          ? previewFile.fileUrl
          : getWorkerFileUrl(previewFile.fileId || previewFile.id);
      setResolvedPreviewUrl(url || '');
    } else {
      setResolvedPreviewUrl('');
    }
  }, [previewFile]);

  // Upload Form State
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadSection, setUploadSection] = useState<'learning' | 'rehab'>('learning');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  // Load files and subscribe to Cloudflare D1 changes
  useEffect(() => {
    let unsubscribe: () => void = () => {};

    const loadData = async () => {
      setLoading(true);
      const records = await fetchCloudFileRecords();
      setFiles(records);
      setLoading(false);

      // Subscribe to real-time updates across devices
      unsubscribe = subscribeToCloudFiles((updatedFiles) => {
        setFiles(updatedFiles);
      });
    };

    loadData();

    return () => {
      unsubscribe();
    };
  }, []);

  // Filtered files list
  const filteredFiles = files.filter(file => {
    if (sectionFilter !== 'all' && file.section !== sectionFilter) return false;

    if (typeFilter === 'video' && file.type !== 'video') return false;
    if (typeFilter === 'pdf' && file.type !== 'pdf') return false;
    if (typeFilter === 'word_ppt' && file.type !== 'word' && file.type !== 'ppt') return false;
    if (typeFilter === 'image' && file.type !== 'image') return false;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      return file.name.toLowerCase().includes(q) || 
             file.moduleTitle.toLowerCase().includes(q) ||
             file.section.toLowerCase().includes(q);
    }

    return true;
  });

  // Upload Mode: 'file' | 'video_link'
  const [uploadMode, setUploadMode] = useState<'file' | 'video_link'>('file');
  const [videoUrl, setVideoUrl] = useState('');

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const ext = file.name.split('.').pop()?.toLowerCase() || '';
      if (file.type.startsWith('video/') || ['mp4', 'mov', 'webm', 'mkv', 'avi', 'flv', 'wmv'].includes(ext)) {
        alert('آپلود مستقیم فایل ویدیو به علت حجم بالا مجاز نیست.\nلطفاً از زبانه «اشتراک‌گذاری لینک ویدیو» برای قرار دادن لینک ویدیو (یوتیوب، آپارات، اینستاگرام، ویمو یا لینک مستقیم) استفاده کنید.');
        e.target.value = '';
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
      if (!uploadTitle) {
        setUploadTitle(file.name.replace(/\.[^/.]+$/, ""));
      }
    }
  };

  const handleUploadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (uploadMode === 'video_link') {
      if (!videoUrl.trim()) return;
      setUploading(true);
      try {
        const newRecord: SharedFileRecord = {
          id: `file_vid_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
          name: uploadTitle.trim() || 'ویدیو آموزشی آنلاین',
          type: 'video',
          section: uploadSection,
          moduleTitle: uploadSection === 'learning' ? 'دستیار یادگیری (ویدیو پیوست)' : 'فیزیوتراپی (ویدیو پیوست)',
          fileUrl: videoUrl.trim(),
          fileSize: 'لینک آنلاین',
          uploadDate: new Date().toISOString(),
          folderPath: `/storage/${uploadSection}/`
        };

        await saveFileRecordToCloud(newRecord);
        const updated = await fetchCloudFileRecords();
        setFiles(updated);

        // Reset
        setVideoUrl('');
        setUploadTitle('');
        setShowUploadModal(false);
      } catch (err) {
        console.error('Video link save failed:', err);
        alert('خطا در ثبت لینک ویدیو.');
      } finally {
        setUploading(false);
      }
      return;
    }

    if (!selectedFile) return;

    setUploading(true);
    try {
      const recordId = `file_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;

      // Upload directly to Cloudflare R2 Worker or R2 Server Proxy
      let r2Key = '';
      let r2FileId = '';
      let r2FileUrl = '';

      const workerRes = await uploadFileToWorker(selectedFile);
      if (workerRes && workerRes.success && workerRes.key) {
        r2Key = workerRes.key;
        r2FileId = workerRes.fileId || '';
        // Always build URL from the R2 key using the Worker /files/ endpoint
        r2FileUrl = getWorkerFileUrl(r2Key);
      } else {
        throw new Error('آپلود به Cloudflare R2 انجام نشد.');
      }

      const fType = detectFileType(selectedFile.name, selectedFile.type);
      const sizeStr = formatBytes(selectedFile.size);

      const newRecord: SharedFileRecord = {
        id: recordId,
        fileId: r2FileId || recordId,
        key: r2Key,
        name: uploadTitle.trim() || selectedFile.name,
        size: selectedFile.size,
        type: fType,
        section: uploadSection,
        moduleTitle: uploadSection === 'learning' ? 'دستیار یادگیری (پیوست عمومی)' : 'فیزیوتراپی و تمرینات (پیوست عمومی)',
        fileUrl: r2FileUrl,
        fileSize: sizeStr,
        uploadDate: new Date().toISOString(),
        folderPath: `/storage/${uploadSection}/`
      };

      await saveFileRecordToCloud(newRecord);
      const updated = await fetchCloudFileRecords();
      setFiles(updated);

      // Reset
      setSelectedFile(null);
      setUploadTitle('');
      setShowUploadModal(false);
    } catch (err: any) {
      console.error('File upload failed:', err);
      alert(`خطا در آپلود فایل: ${err.message || 'ناشناخته'}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileId: string, fileName: string) => {
    let confirmed = false;
    try {
      if (typeof window !== 'undefined' && window.confirm) {
        confirmed = window.confirm(`آیا از حذف فایل "${fileName}" اطمینان دارید؟`);
      }
    } catch {
      confirmed = true;
    }
    if (!confirmed) return;

    const targetFile = files.find(f => f.id === fileId);
    const r2Key = targetFile?.key || targetFile?.fileId || targetFile?.id || fileId;

    // Step 1: Delete from Cloudflare R2 first
    let r2Deleted = false;
    if (r2Key && !r2Key.startsWith('http')) {
      r2Deleted = await deleteFileFromWorker(r2Key);
    } else {
      // Video links or external URLs have no R2 object — treat as success
      r2Deleted = true;
    }

    if (!r2Deleted) {
      // Warn but still proceed — the file may already be gone from R2
      console.warn('[Delete] Worker did not confirm R2 deletion for key:', r2Key);
    }

    // Step 2: Remove metadata from Cloudflare D1 and update UI
    setFiles(prev => prev.filter(f => f.id !== fileId));
    await deleteCloudFileRecord(fileId);

    // Step 3: Show success message
    setDeleteStatus({ message: `فایل «${fileName}» با موفقیت حذف شد.`, type: 'success' });
    setTimeout(() => setDeleteStatus(null), 3500);

    const updated = await fetchCloudFileRecords();
    setFiles(updated);
  };

  const handleClearAll = async () => {
    let confirmed = true;
    try {
      if (typeof window !== 'undefined' && window.confirm) {
        confirmed = window.confirm('آیا از حذف تمام فایل‌های موجود در مرکز همگام‌سازی ابری اطمینان دارید؟');
      }
    } catch {
      confirmed = true;
    }
    if (confirmed) {
      setFiles([]);
      await Promise.all([
        clearAllCloudFiles(),
        clearAllLearningData()
      ]);
    }
  };

  const getBadgeColor = (type: SharedFileRecord['type']) => {
    switch (type) {
      case 'video': return 'bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300 border-purple-200 dark:border-purple-800';
      case 'ppt': return 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300 border-amber-200 dark:border-amber-800';
      case 'word': return 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300 border-blue-200 dark:border-blue-800';
      case 'pdf': return 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300 border-red-200 dark:border-red-800';
      case 'image': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800';
      default: return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 border-gray-200 dark:border-gray-700';
    }
  };

  const getIcon = (type: SharedFileRecord['type']) => {
    switch (type) {
      case 'video': return <Video className="w-5 h-5 text-purple-500" />;
      case 'ppt': return <Presentation className="w-5 h-5 text-amber-500" />;
      case 'word': return <FileText className="w-5 h-5 text-blue-500" />;
      case 'pdf': return <FileText className="w-5 h-5 text-red-500" />;
      case 'image': return <Film className="w-5 h-5 text-emerald-500" />;
      default: return <File className="w-5 h-5 text-gray-500" />;
    }
  };

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-3xl p-5 md:p-7 shadow-xl space-y-6 animate-fadeIn">

      {/* Delete status toast */}
      {deleteStatus && (
        <div className={`fixed top-5 left-1/2 -translate-x-1/2 z-[9999] px-5 py-3 rounded-2xl shadow-2xl text-sm font-bold flex items-center gap-2 animate-fadeIn border ${
          deleteStatus.type === 'success'
            ? 'bg-emerald-600 text-white border-emerald-500'
            : 'bg-red-600 text-white border-red-500'
        }`}>
          {deleteStatus.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <X className="w-4 h-4" />}
          {deleteStatus.message}
        </div>
      )}

      {/* Header Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-5 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-indigo-500/10 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400 rounded-2xl shrink-0">
            <Folder className="w-7 h-7" />
          </div>
          <div>
            <div className="flex items-center gap-2 text-xs font-bold text-indigo-600 dark:text-indigo-400 mb-0.5">
              <Cloud className="w-4 h-4 text-emerald-500" />
              <span>پوشه یکپارچه ابری (Cloud Sync Hub)</span>
            </div>
            <h2 className="text-lg md:text-xl font-extrabold text-gray-900 dark:text-white font-display">
              مرکز ذخیره‌سازی فایل‌ها و پیوست‌ها
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              مدیریت و مشاهده تمامی فایل‌های ویدیو، PDF، Word، پاورپوینت و تصاویر در دستگاه‌های iOS, Android و Chrome.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {files.length > 0 && (
            <button
              onClick={handleClearAll}
              className="bg-red-50 hover:bg-red-100 text-red-600 dark:bg-red-950/50 dark:hover:bg-red-900/60 dark:text-red-400 px-3 py-2.5 rounded-xl text-xs font-bold border border-red-200 dark:border-red-800 flex items-center gap-1.5 transition-all cursor-pointer"
              title="پاکسازی تمام فایل‌های ابری"
            >
              <Trash2 className="w-4 h-4" />
              <span>حذف همه ({files.length})</span>
            </button>
          )}
          <button
            onClick={() => setShowUploadModal(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2.5 rounded-xl text-xs font-bold shadow-md flex items-center gap-2 transition-all cursor-pointer"
          >
            <UploadCloud className="w-4 h-4" />
            آپلود فایل جدید در پوشه
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Filters & Search Bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-gray-400 absolute right-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="جستجوی نام فایل، عنوان جلسه یا حرکت ورزشی..."
            className="w-full bg-slate-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl pr-10 pl-3 py-2 text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* Section Filter */}
        <div className="flex items-center gap-1 bg-slate-100 dark:bg-gray-800/80 p-1 rounded-xl text-xs shrink-0">
          <button
            onClick={() => setSectionFilter('all')}
            className={`px-3 py-1.5 rounded-lg font-bold transition-all cursor-pointer ${
              sectionFilter === 'all' 
                ? 'bg-white dark:bg-gray-700 text-indigo-600 dark:text-white shadow-xs' 
                : 'text-gray-500 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            همه بخش‌ها ({files.length})
          </button>
          <button
            onClick={() => setSectionFilter('learning')}
            className={`px-3 py-1.5 rounded-lg font-bold transition-all cursor-pointer ${
              sectionFilter === 'learning' 
                ? 'bg-white dark:bg-gray-700 text-indigo-600 dark:text-white shadow-xs' 
                : 'text-gray-500 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            🎓 دستیار یادگیری ({files.filter(f => f.section === 'learning').length})
          </button>
          <button
            onClick={() => setSectionFilter('rehab')}
            className={`px-3 py-1.5 rounded-lg font-bold transition-all cursor-pointer ${
              sectionFilter === 'rehab' 
                ? 'bg-white dark:bg-gray-700 text-indigo-600 dark:text-white shadow-xs' 
                : 'text-gray-500 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            🧘 فیزیوتراپی ({files.filter(f => f.section === 'rehab').length})
          </button>
        </div>
      </div>

      {/* File Type Filter Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 text-xs no-scrollbar border-b border-gray-100 dark:border-gray-800">
        <button
          onClick={() => setTypeFilter('all')}
          className={`px-3 py-1.5 rounded-xl font-bold whitespace-nowrap transition-all cursor-pointer ${
            typeFilter === 'all'
              ? 'bg-indigo-600 text-white shadow-sm'
              : 'bg-slate-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-slate-200'
          }`}
        >
          تمام فرمت‌ها
        </button>
        <button
          onClick={() => setTypeFilter('video')}
          className={`px-3 py-1.5 rounded-xl font-bold whitespace-nowrap flex items-center gap-1.5 transition-all cursor-pointer ${
            typeFilter === 'video'
              ? 'bg-purple-600 text-white shadow-sm'
              : 'bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 hover:bg-purple-100'
          }`}
        >
          <Video className="w-3.5 h-3.5" />
          ویدیوها ({files.filter(f => f.type === 'video').length})
        </button>
        <button
          onClick={() => setTypeFilter('pdf')}
          className={`px-3 py-1.5 rounded-xl font-bold whitespace-nowrap flex items-center gap-1.5 transition-all cursor-pointer ${
            typeFilter === 'pdf'
              ? 'bg-red-600 text-white shadow-sm'
              : 'bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 hover:bg-red-100'
          }`}
        >
          <FileText className="w-3.5 h-3.5" />
          اسناد PDF ({files.filter(f => f.type === 'pdf').length})
        </button>
        <button
          onClick={() => setTypeFilter('word_ppt')}
          className={`px-3 py-1.5 rounded-xl font-bold whitespace-nowrap flex items-center gap-1.5 transition-all cursor-pointer ${
            typeFilter === 'word_ppt'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 hover:bg-blue-100'
          }`}
        >
          <Presentation className="w-3.5 h-3.5" />
          Word و پاورپوینت ({files.filter(f => f.type === 'word' || f.type === 'ppt').length})
        </button>
        <button
          onClick={() => setTypeFilter('image')}
          className={`px-3 py-1.5 rounded-xl font-bold whitespace-nowrap flex items-center gap-1.5 transition-all cursor-pointer ${
            typeFilter === 'image'
              ? 'bg-emerald-600 text-white shadow-sm'
              : 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100'
          }`}
        >
          <Film className="w-3.5 h-3.5" />
          تصاویر ({files.filter(f => f.type === 'image').length})
        </button>
      </div>

      {/* File Grid */}
      {loading ? (
        <div className="p-12 text-center text-xs text-gray-400 flex flex-col items-center justify-center gap-2">
          <RefreshCw className="w-6 h-6 animate-spin text-indigo-500" />
          <span>در حال دریافت لیست فایل‌های همگام‌شده ابری...</span>
        </div>
      ) : filteredFiles.length === 0 ? (
        <div className="p-10 text-center bg-slate-50 dark:bg-gray-800/40 border border-dashed border-gray-200 dark:border-gray-700 rounded-2xl space-y-3">
          <div className="w-12 h-12 bg-indigo-50 dark:bg-indigo-950 text-indigo-500 rounded-2xl flex items-center justify-center mx-auto">
            <Folder className="w-6 h-6" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-gray-800 dark:text-gray-200">
              هیچ فایلی با این مشخصات یافت نشد
            </h4>
            <p className="text-xs text-gray-400 mt-1 max-w-sm mx-auto">
              شما می‌توانید با دکمه "آپلود فایل جدید"، ویدیو، PDF، Word یا هر اسنادی را در این پوشه قرار دهید تا در همه دستگاه‌هایتان (iOS و Android و وب) همگام گردد.
            </p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
          {filteredFiles.map((file) => (
            <div
              key={file.id}
              className="bg-slate-50 dark:bg-gray-800/80 border border-slate-200/90 dark:border-gray-700/80 hover:border-indigo-300 dark:hover:border-indigo-700 rounded-2xl p-4 transition-all group flex flex-col justify-between space-y-3 shadow-xs hover:shadow-md"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="p-2.5 bg-white dark:bg-gray-900 rounded-xl shadow-xs shrink-0 mt-0.5">
                    {getIcon(file.type)}
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-xs font-bold text-gray-900 dark:text-white truncate" title={file.name}>
                      {file.name}
                    </h4>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                      مربوط به: <span className="font-semibold text-gray-800 dark:text-gray-200">{file.moduleTitle}</span>
                    </p>
                    <div className="flex items-center gap-2 text-[10px] text-gray-400 mt-1.5">
                      <span className={`px-2 py-0.5 rounded-md font-bold border ${getBadgeColor(file.type)} uppercase`}>
                        {file.type}
                      </span>
                      {file.fileSize && <span>{file.fileSize}</span>}
                      <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-bold">
                        <CheckCircle2 className="w-3 h-3" />
                        ذخیره در ابَر
                      </span>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => handleDelete(file.id, file.name)}
                  className="text-gray-400 hover:text-red-500 p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors cursor-pointer"
                  title="حذف فایل"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              {/* Action Buttons */}
              <div className="pt-2 border-t border-gray-200/60 dark:border-gray-700/60 flex items-center justify-between gap-2">
                <span className="text-[10px] text-gray-400 font-mono">
                  بخش: {file.section === 'learning' ? 'دستیار یادگیری' : 'فیزیوتراپی'}
                </span>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPreviewFile(file)}
                    className="px-3 py-1.5 bg-indigo-50 dark:bg-indigo-950/50 hover:bg-indigo-100 text-indigo-600 dark:text-indigo-400 rounded-lg text-xs font-bold flex items-center gap-1 transition-colors cursor-pointer"
                  >
                    {file.type === 'video' ? <Play className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    <span>{file.type === 'video' ? 'پخش ویدیو' : 'مشاهده'}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => triggerFileDownload(file.fileUrl, file.name, file.id, file.key)}
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold flex items-center gap-1 transition-colors cursor-pointer"
                  >
                    <Download className="w-3.5 h-3.5" />
                    دانلود
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Preview File */}
      {previewFile && (
        <div className="fixed inset-0 z-50 bg-gray-950/80 backdrop-blur-xs flex items-center justify-center p-3 md:p-6 animate-fadeIn">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
            <div className="p-4 bg-gray-50 dark:bg-gray-800/80 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                {getIcon(previewFile.type)}
                <h4 className="text-sm font-bold text-gray-900 dark:text-white truncate font-display">
                  {previewFile.name}
                </h4>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => triggerFileDownload(previewFile.fileUrl, previewFile.name, previewFile.id, previewFile.key)}
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold flex items-center gap-1 cursor-pointer"
                >
                  <Download className="w-3.5 h-3.5" />
                  دانلود نسخه اصلی
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewFile(null)}
                  className="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-lg cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-4 flex-1 overflow-auto bg-slate-950/10 dark:bg-gray-950 flex flex-col items-center justify-center min-h-[300px]">
              {previewFile.type === 'video' && (() => {
                const srcUrl = resolvedPreviewUrl || previewFile.fileUrl;
                const embedUrl = getEmbedVideoUrl(srcUrl);
                const isInstagram = srcUrl.includes('instagram.com') || srcUrl.includes('instagr.am');
                if (embedUrl) {
                  return (
                    <div className={`w-full ${isInstagram ? 'h-[500px] max-w-sm mx-auto' : 'aspect-video max-w-3xl h-[60vh]'}`}>
                      <iframe
                        src={embedUrl}
                        title={previewFile.name}
                        className="w-full h-full rounded-xl border-0 shadow-2xl"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  );
                }
                return (
                  <video src={srcUrl} controls autoPlay className="w-full max-w-3xl rounded-xl shadow-2xl max-h-[70vh]" />
                );
              })()}
              {previewFile.type === 'image' && (
                <img src={resolvedPreviewUrl || previewFile.fileUrl} alt={previewFile.name} className="max-w-full max-h-[70vh] rounded-xl shadow-lg object-contain" />
              )}
              {previewFile.type === 'pdf' && (
                <iframe
                  src={resolvedPreviewUrl || getWorkerFileUrl(previewFile.key || previewFile.fileId || previewFile.id)}
                  title={previewFile.name}
                  className="w-full h-[70vh] rounded-xl border border-gray-300 dark:border-gray-700 bg-white"
                />
              )}
              {(previewFile.type === 'ppt' || previewFile.type === 'word' || previewFile.type === 'other') && (
                <div className="text-center space-y-4 max-w-md p-6 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-xl">
                  <div className="w-16 h-16 rounded-2xl bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 flex items-center justify-center mx-auto">
                    {getIcon(previewFile.type)}
                  </div>
                  <div>
                    <h5 className="font-bold text-sm text-gray-900 dark:text-white mb-1">
                      سند آموزشی ({previewFile.type.toUpperCase()})
                    </h5>
                    <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                      فایل <strong className="text-gray-800 dark:text-gray-200">{previewFile.name}</strong> آماده دانلود است.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => triggerFileDownload(previewFile.fileUrl, previewFile.name, previewFile.id, previewFile.key)}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-2 shadow-md cursor-pointer"
                  >
                    <Download className="w-4 h-4" />
                    دانلود و مشاهده
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Upload New File */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 bg-gray-950/70 backdrop-blur-xs flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-lg p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-gray-800">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2 font-display">
                <UploadCloud className="w-5 h-5 text-indigo-600" />
                آپلود فایل جدید به پوشه ابری
              </h3>
              <button
                type="button"
                onClick={() => setShowUploadModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleUploadSubmit} className="space-y-4">
              {/* Upload Mode Switcher */}
              <div className="flex rounded-xl bg-slate-100 dark:bg-gray-800 p-1 text-xs">
                <button
                  type="button"
                  onClick={() => setUploadMode('file')}
                  className={`flex-1 py-1.5 rounded-lg font-bold transition-all cursor-pointer ${
                    uploadMode === 'file' 
                      ? 'bg-white dark:bg-gray-700 text-indigo-600 dark:text-white shadow-xs' 
                      : 'text-gray-500 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  📄 آپلود فایل (PDF، Word، پاورپوینت)
                </button>
                <button
                  type="button"
                  onClick={() => setUploadMode('video_link')}
                  className={`flex-1 py-1.5 rounded-lg font-bold transition-all cursor-pointer ${
                    uploadMode === 'video_link' 
                      ? 'bg-purple-600 text-white shadow-xs' 
                      : 'text-gray-500 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  🎥 اشتراک‌گذاری لینک ویدیو
                </button>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                  انتخاب بخش مربوطه
                </label>
                <select
                  value={uploadSection}
                  onChange={(e) => setUploadSection(e.target.value as any)}
                  className="w-full bg-slate-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2 text-xs text-gray-900 dark:text-white"
                >
                  <option value="learning">🎓 دستیار یادگیری (Learning Assistant)</option>
                  <option value="rehab">🧘 فیزیوتراپی و تمرینات اصلاحی (Rehab Module)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                  عنوان یا نام {uploadMode === 'video_link' ? 'ویدیو' : 'فایل'}
                </label>
                <input
                  type="text"
                  value={uploadTitle}
                  onChange={(e) => setUploadTitle(e.target.value)}
                  placeholder={uploadMode === 'video_link' ? "مثلاً: فیلم آموزشی جلسه سوم یوتیوب/آپارات" : "مثلاً: جزوه PDF یا پاورپوینت"}
                  className="w-full bg-slate-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2 text-xs text-gray-900 dark:text-white"
                />
              </div>

              {uploadMode === 'file' ? (
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    انتخاب فایل (PDF، Word، پاورپوینت، تصویر)
                  </label>
                  <input
                    type="file"
                    required
                    accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/*,.pdf,.doc,.docx,.ppt,.pptx,.txt,.csv,.json,.md,.heic,.heif"
                    onChange={handleFileSelect}
                    className="w-full text-xs text-gray-500 file:mr-2 file:py-2 file:px-3 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-indigo-600 file:text-white hover:file:bg-indigo-700 cursor-pointer"
                  />
                  <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1">
                    * آپلود مستقیم فایل ویدیو به علت حجم بالا پشتیبانی نمی‌شود. لطفاً از زبانه «اشتراک‌گذاری لینک ویدیو» استفاده فرمایید.
                  </p>
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    آدرس / لینک ویدیو (یوتیوب، آپارات، اینستاگرام، ویمو یا لینک مستقیم) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="url"
                    required
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=... یا https://www.aparat.com/v/..."
                    className="w-full bg-slate-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2 text-xs text-gray-900 dark:text-white font-mono dir-ltr"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">
                    ویدیوهای یوتیوب، آپارات، اینستاگرام و ویمو به صورت مستقیم درون برنامه پخش خواهند شد.
                  </p>
                </div>
              )}

              <div className="pt-3 flex items-center justify-end gap-2 border-t border-gray-100 dark:border-gray-800">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl text-xs font-semibold cursor-pointer"
                >
                  انصراف
                </button>
                <button
                  type="submit"
                  disabled={uploading || (uploadMode === 'file' ? !selectedFile : !videoUrl.trim())}
                  className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold flex items-center gap-2 shadow-md cursor-pointer disabled:opacity-50"
                >
                  {uploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
                  {uploadMode === 'video_link' ? 'ثبت و اشتراک ویدیو' : 'آپلود و ذخیره در ابَر'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Full Screen File Uploading Lock Overlay */}
      {uploading && (
        <div className="fixed inset-0 z-[100] bg-gray-950/80 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn dir-rtl cursor-wait select-none">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl text-center space-y-5 animate-scaleUp">
            <div className="relative w-20 h-20 mx-auto flex items-center justify-center">
              <div className="absolute inset-0 rounded-full border-4 border-indigo-200 dark:border-indigo-900/60 animate-ping opacity-75"></div>
              <div className="w-20 h-20 rounded-full bg-indigo-50 dark:bg-indigo-950/80 border-2 border-indigo-500/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400 shadow-inner">
                <UploadCloud className="w-9 h-9 animate-bounce" />
              </div>
            </div>

            <div className="space-y-2">
              <h3 className="font-extrabold text-base md:text-lg text-gray-900 dark:text-white">
                در حال آپلود و ذخیره‌سازی فایل در حافظه ابری...
              </h3>
              {selectedFile && (
                <p className="text-xs text-indigo-600 dark:text-indigo-400 font-bold bg-indigo-50 dark:bg-indigo-950/50 px-3 py-1.5 rounded-xl inline-block max-w-full truncate dir-ltr">
                  {selectedFile.name}
                </p>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed pt-1">
                لطفاً شکیبا باشید. فایل شما در حال ذخیره روی سرور پروژه و اشتراک‌گذاری همزمان است.
              </p>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-2.5 overflow-hidden">
              <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-600 h-2.5 rounded-full animate-pulse w-full"></div>
            </div>

            <div className="flex items-center justify-center gap-2 text-[11px] font-bold text-gray-400 dark:text-gray-500 pt-1">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-500" />
              صفحه به طور خودکار پس از پایان آپلود آزاد خواهد شد...
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
